import React, { useCallback } from 'react';

import { Box, Button, Popover } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconDirection } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';

import ActionButton from '../ActionButton/ActionButton';

import { NO_RUNNING_ACTIONS } from './GroupDropdown.constants';
import {
  groupButtonSx,
  popoverAnchorOrigin,
  popoverContentSx,
  popoverSlotProps,
  popoverTransformOrigin,
} from './GroupDropdown.styles';
import { GroupDropdownTestIds } from './GroupDropdown.testIds';

const GroupDropdown = ({
  groupName,
  actions,
  executingActionId = null,
  disabledLoanActionsSet,
  runningLoanActionsSet = NO_RUNNING_ACTIONS,
  onConfirmLoanAction,
}) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const onActionClick = useCallback(
    event => {
      onConfirmLoanAction(event);
      onClearAnchorElement();
    },
    [onClearAnchorElement, onConfirmLoanAction],
  );

  return (
    <>
      <Button
        aria-selected={!!anchorEl}
        color="inherit"
        data-testid={GroupDropdownTestIds.GROUP_BUTTON}
        endIcon={<ArrowCheckIcon direction={IconDirection.DOWN} />}
        sx={groupButtonSx}
        variant="outlined"
        onClick={onSetAnchorElement}
      >
        {groupName}
      </Button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={popoverAnchorOrigin}
        open={!!anchorEl}
        slotProps={popoverSlotProps}
        transformOrigin={popoverTransformOrigin}
        onClose={onClearAnchorElement}
      >
        <Box sx={popoverContentSx}>
          {actions.map(actionData => (
            <ActionButton
              color={actionData.color}
              dataId={actionData.id}
              dataTestId={GroupDropdownTestIds.getActionButtonId(actionData.id)}
              disabled={
                executingActionId === actionData.id || disabledLoanActionsSet.has(actionData.id)
              }
              icon={actionData.icon}
              isRunning={runningLoanActionsSet.has(actionData.id)}
              key={actionData.id}
              name={actionData.name}
              variant={actionData.variant}
              onClick={onActionClick}
            >
              {actionData.name}
            </ActionButton>
          ))}
        </Box>
      </Popover>
    </>
  );
};

GroupDropdown.propTypes = {
  actions: PropTypes.array.isRequired,
  disabledLoanActionsSet: PropTypes.instanceOf(Set).isRequired,
  groupName: PropTypes.string.isRequired,
  onConfirmLoanAction: PropTypes.func.isRequired,
  executingActionId: PropTypes.string,
  runningLoanActionsSet: PropTypes.instanceOf(Set),
};

export default GroupDropdown;
